import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { t } from 'i18next';
import { sqStoredTableStore } from '@/core/core.stores';
import { refreshStoredTables, updateStoredTable } from '@/itemFinder/itemFinder.actions';
import { setAutoFocusStoredTableTab, setStoredTableEditorVisible } from '@/main/layout.actions';
import { getMaterializedTable } from '@/tableDefinitionEditor/materializedTable.utilities';
import { updateTableDefinitionName } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { setDisplayStoredTableEditor } from '@/worksheet/worksheet.actions';
import { sqDatasourcesApi } from '@seeqdev/api/endpoint/DatasourcesApi';
import { sqStoredTablesApi } from '@seeqdev/api/endpoint/StoredTablesApi';
import type { CellValueChangedEvent, ColDef, Column } from 'ag-grid-enterprise';
import type {
  MaterializedTableOutput,
  ProcessedMaterializedTable,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnDataUpdateInputListV1 } from '@seeqdev/api/model/ColumnDataUpdateInputListV1';
import type { ColumnDataUpdateInputV1 } from '@seeqdev/api/model/ColumnDataUpdateInputV1';
import type { DatasourceOutputV1 } from '@seeqdev/api/model/DatasourceOutputV1';
import type { StoredTableColumnInputV1 } from '@seeqdev/api/model/StoredTableColumnInputV1';
import type { StoredTableColumnOutputV1 } from '@seeqdev/api/model/StoredTableColumnOutputV1';

export const STORED_TABLE_CANCELLATION_GROUP = 'stored-table';

export async function getExistingStoredTable(storedTableId: string) {
  try {
    sqStoredTableStore.actions.setIsLoadingStoredTable(true);
    const { data: storedTableData } = await sqStoredTablesApi.getStoredTable({ id: storedTableId });
    sqStoredTableStore.actions.setStoredTable(storedTableData);

    const output = await getMaterializedTable(
      storedTableId,
      storedTableData.columns.map((column) => column.columnName),
      [],
      STORED_TABLE_CANCELLATION_GROUP,
    );
    const newTable: MaterializedTableOutput | undefined = output ? output : undefined;
    sqStoredTableStore.actions.setMaterializedTable(newTable);
    sqStoredTableStore.actions.setIsLoadingStoredTable(false);

    return storedTableData;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

export function editStoredTable(storedTableId: string) {
  setAutoFocusStoredTableTab(true);
  setDisplayStoredTableEditor(true);
  sqStoredTableStore.actions.reset();
  sqStoredTableStore.actions.setTableId(storedTableId);
}

export const buildRowDataForAgGrid = (
  agGridColumnDefinitions: ColDef[],
  materializedTable: ProcessedMaterializedTable | undefined,
) => {
  if (!materializedTable) {
    return null;
  }

  const materializedTableHeaders = materializedTable.headers;

  const rows = materializedTable.rows;
  return rows.map((row) => {
    const rowData: Record<string, any> = {};
    materializedTableHeaders.forEach((header, index: number) => {
      const agColumn = agGridColumnDefinitions.find((columnDef) => columnDef.headerName === header.name);
      const field = agColumn?.field;
      if (field) {
        rowData[field] = row[index];
      }
    });
    return rowData;
  });
};

export const handleCellValueChanged = async (
  event: CellValueChangedEvent,
  columns: StoredTableColumnOutputV1[],
  tableId: string | undefined,
) => {
  const uniqueIdColumn = columns.find((c) => c.columnName === SeeqNames.MaterializedTables.UniqueRowId)?.id;
  const column = columns.find((c) => c.columnName === event.colDef.headerName);
  const dataUpdates: ColumnDataUpdateInputV1[] = [];

  if (column && uniqueIdColumn && tableId) {
    const updateValue = event.data[column.id].value;
    const rowIdValue = event.data[uniqueIdColumn]?.value;
    const updateUom = event.data[column.id].uom;

    if (rowIdValue === undefined || updateValue === undefined) {
      resetValue(event, column);
      errorToast({ messageKey: 'STORED_TABLE.UNABLE_TO_UPDATE_CELL' });
      return;
    }

    dataUpdates.push({
      rowId: rowIdValue,
      value: updateValue,
      uom: updateUom,
    });
    const input: ColumnDataUpdateInputListV1 = { dataUpdates };

    try {
      await sqStoredTablesApi.updateColumnData(input, {
        id: tableId,
        columnId: column.id,
      });

      successToast({ messageKey: 'STORED_TABLE.DATA_UPDATED' });
      await getExistingStoredTable(tableId);
    } catch (e) {
      resetValue(event, column);
      errorToast({ httpResponseOrError: e });
    }
  } else {
    errorToast({ messageKey: 'STORED_TABLE.UNABLE_TO_UPDATE_CELL' });
  }
  event.api.clearFocusedCell();
};

function resetValue(event: CellValueChangedEvent, column: StoredTableColumnOutputV1) {
  event.data[column.id] = event.oldValue;
  event.api.refreshCells({ columns: [event.column], rowNodes: [event.node] });
}

export const onTableReload = (newTable?: MaterializedTableOutput) => {
  sqStoredTableStore.actions.setMaterializedTable(newTable);
};

export const storedTableColumnOutputToInput = (column: StoredTableColumnOutputV1): StoredTableColumnInputV1 => {
  return {
    columnType: column.columnType,
    columnUom: column.columnUom,
    columnName: column.columnName,
    isIndexed: column.isIndexed,
    isHidden: column.isHidden,
    sortAscending: column.sortAscending,
    sortIndex: column.sortIndex,
    description: column.description,
  };
};

export const closeStoredTableEditor = () => {
  setDisplayStoredTableEditor(false);
  setStoredTableEditorVisible(false);
};

export const deleteStoredTableColumn = async (column: StoredTableColumnOutputV1, tableId: string) => {
  try {
    await sqStoredTablesApi.deleteColumnFromStoredTable({ id: tableId, columnId: column.id });
    await getExistingStoredTable(tableId);
    await updateStoredTable(tableId);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Retrieves the data source for a table definition.
 */
export const getStoredTableDataSource = async (storedTableId: string): Promise<DatasourceOutputV1 | undefined> => {
  const {
    data: { datasources },
  } = await sqDatasourcesApi.getDatasources({
    datasourceId: storedTableId.toLocaleLowerCase(),
  });

  return datasources?.at(0);
};

export const handleUpdateTableName = async (newName: string | undefined, tableId: string | undefined) => {
  const trimmedTableName = newName?.trim();
  if (trimmedTableName) {
    if (tableId && trimmedTableName !== sqStoredTableStore.getState().name) {
      try {
        await updateTableDefinitionName({ tableDefinitionId: tableId, name: trimmedTableName });
        sqStoredTableStore.actions.setStoredTableName(trimmedTableName);
        await refreshStoredTables();
      } catch (e) {
        errorToast({ messageKey: t('ITEM_FINDER.UPDATE_NAME_TABLE_DEFINITION_ERROR') });
      }
    }
  }
};

export const deleteStoredTableRows = async (tableId: string, rowIds: string[]) => {
  try {
    const response = await sqStoredTablesApi.deleteStoredTableRows(
      {
        rowIds,
      },
      {
        tableId,
      },
    );
    return response.data;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return { deletedRowCount: 0 };
  }
};

export const reorderColumns = async (
  tableId: string,
  columns: StoredTableColumnOutputV1[],
  agGridColumns: Column[],
  affectedColumnId: string,
  toIndex: number,
) => {
  const columnsIdsNotInGrid = columns
    .filter((column) => !agGridColumns.find((agGridColumn) => agGridColumn.getColDef().field === column.id))
    .map((column) => column.id);

  const columnsIdsInGridInNewOrder = agGridColumns
    .map((agColumn) => agColumn.getColDef().field)
    .filter((columnId) => columnId !== affectedColumnId)
    .filter((columnId): columnId is string => columnId !== undefined);

  columnsIdsInGridInNewOrder.splice(toIndex, 0, affectedColumnId);
  const newOrder = columnsIdsNotInGrid.concat(columnsIdsInGridInNewOrder);
  try {
    const { data: updatedStoredTable } = await sqStoredTablesApi.reorderColumnsInStoredTable(
      { columnDefinitionIds: newOrder },
      { id: tableId },
    );
    sqStoredTableStore.actions.setStoredTable(updatedStoredTable);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};
